import { supabase } from '../supabaseClient';
import type { ContractTemplate, TemplateType, SharePointFile } from '@shared/types';

// Helper to transform database row to ContractTemplate object
function transformTemplate(row: any): ContractTemplate {
  return {
    id: row.id,
    name: row.name,
    templateType: row.template_type,
    description: row.description,
    sharepointPath: row.sharepoint_path,
    sharepointFileId: row.sharepoint_file_id,
    sharepointFileName: row.sharepoint_file_name,
    sharepointDriveId: row.sharepoint_drive_id,
    namingConvention: row.naming_convention,
    hasScope: row.has_scope,
    hasDeliverables: row.has_deliverables,
    hasMilestones: row.has_milestones,
    hasPaymentSchedule: row.has_payment_schedule,
    hasLicensing: row.has_licensing || false,
    supportsRecurringPayments: row.supports_recurring_payments,
    defaultClauses: row.default_clauses || [],
    defaultPaymentTerms: row.default_payment_terms || {},
    metadata: row.metadata || {},
    isActive: row.is_active,
    version: row.version,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const templateService = {
  async getTemplates(filters?: { type?: TemplateType }) {
    let query = supabase
      .from('contract_templates')
      .select('*')
      .eq('is_active', true)
      .order('template_type', { ascending: true })
      .order('name', { ascending: true });

    if (filters?.type) {
      query = query.eq('template_type', filters.type);
    }

    const { data, error } = await query;
    
    if (error) throw error;
    return data ? data.map(transformTemplate) : [];
  },

  async getTemplate(id: string) {
    const { data, error } = await supabase
      .from('contract_templates')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return transformTemplate(data);
  },

  async createTemplate(template: Partial<ContractTemplate>) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error} = await supabase
      .from('contract_templates')
      .insert([{
        name: template.name,
        template_type: template.templateType,
        description: template.description || null,
        sharepoint_path: template.sharepointPath,
        sharepoint_file_id: template.sharepointFileId || null,
        sharepoint_file_name: template.sharepointFileName || null,
        sharepoint_drive_id: template.sharepointDriveId || null,
        naming_convention: template.namingConvention || null,
        has_scope: template.hasScope ?? true,
        has_deliverables: template.hasDeliverables ?? true,
        has_milestones: template.hasMilestones ?? true,
        has_payment_schedule: template.hasPaymentSchedule ?? true,
        has_licensing: template.hasLicensing ?? false,
        supports_recurring_payments: template.supportsRecurringPayments ?? false,
        default_clauses: template.defaultClauses || [],
        default_payment_terms: template.defaultPaymentTerms || {},
        metadata: template.metadata || {},
        is_active: true,
        version: 1,
        created_by: user.id,
      }])
      .select()
      .single();
    
    if (error) {
      console.error('Create template error:', error);
      throw error;
    }
    
    return transformTemplate(data);
  },

  async updateTemplate(id: string, updates: Partial<ContractTemplate>) {
    const { data, error } = await supabase
      .from('contract_templates')
      .update({
        name: updates.name,
        template_type: updates.templateType,
        description: updates.description || null,
        sharepoint_path: updates.sharepointPath,
        sharepoint_file_id: updates.sharepointFileId || null,
        sharepoint_file_name: updates.sharepointFileName || null,
        sharepoint_drive_id: updates.sharepointDriveId || null,
        naming_convention: updates.namingConvention || null,
        has_scope: updates.hasScope,
        has_deliverables: updates.hasDeliverables,
        has_milestones: updates.hasMilestones,
        has_payment_schedule: updates.hasPaymentSchedule,
        has_licensing: updates.hasLicensing,
        supports_recurring_payments: updates.supportsRecurringPayments,
        default_clauses: updates.defaultClauses,
        default_payment_terms: updates.defaultPaymentTerms,
        metadata: updates.metadata,
        is_active: updates.isActive,
      })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return transformTemplate(data);
  },

  async listSharePointTemplateFiles(): Promise<SharePointFile[]> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-list-template-files`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list SharePoint template files');
    }

    const data = await response.json();
    return data.files || [];
  },
};

